import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Download } from "lucide-react";
import api, { fileToBase64 } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const emptyService = { name: "", description: "", details: "", price: "", unit: "", tax: "ttc", category: "", photo: "", published: true, order: 0 };
const SERVICE_CATEGORIES = [
  { id: "bureaux-commerces", label: "Bureaux & commerces" },
  { id: "maisons-appartements", label: "Maisons & appartements" },
  { id: "remise-en-etat", label: "Remise en état" },
  { id: "vitrines-communs", label: "Vitrines & parties communes" },
];
const QUOTE_STATUS = {
  nouvelle: "Nouvelle",
  en_cours: "En cours",
  devis_envoye: "Devis envoyé",
  acceptee: "Acceptée",
  terminee: "Terminée",
  annulee: "Annulée",
  traitee: "Traitée",
};
const APPLICATION_STATUS = {
  nouvelle: "Nouvelle",
  en_cours: "En cours",
  retenue: "Retenue",
  refusee: "Refusée",
};
const BOOKING_STATUS = {
  en_attente_paiement: "En attente de paiement",
  confirmee: "Confirmée",
  terminee: "Terminée",
  annulee: "Annulée",
};

export default function ServicesManager() {
  const [sub, setSub] = useState("prestations");
  const [items, setItems] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/services").then((r) => setItems(r.data)).catch(() => {});
    api.get("/admin/service-quotes").then((r) => setQuotes(r.data)).catch(() => {});
    api.get("/admin/service-bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/applications").then((r) => setApplications(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...editing,
        price: parseFloat(String(editing.price).replace(",", ".")) || 0,
        order: parseInt(editing.order, 10) || 0,
      };
      if (editing.id) {
        await api.put(`/admin/services/${editing.id}`, payload);
      } else {
        await api.post("/admin/services", payload);
      }
      toast.success("Prestation enregistrée.");
      setEditing(null);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/services/${id}`);
    toast.success("Prestation supprimée.");
    load();
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await fileToBase64(file);
    setEditing((s) => ({ ...s, photo: base64 }));
  };

  const set = (key) => (e) => setEditing((s) => ({ ...s, [key]: e.target.value }));

  const setQuoteStatus = async (id, status) => {
    await api.patch(`/admin/service-quotes/${id}`, { status });
    toast.success("Statut mis à jour.");
    load();
  };
  const removeQuote = async (id) => {
    await api.delete(`/admin/service-quotes/${id}`);
    toast.success("Demande supprimée.");
    load();
  };
  const setBookingStatus = async (id, status) => {
    await api.patch(`/admin/service-bookings/${id}`, { status });
    toast.success("Statut mis à jour.");
    load();
  };
  const removeBooking = async (id) => {
    await api.delete(`/admin/service-bookings/${id}`);
    toast.success("Réservation supprimée.");
    load();
  };

  const setApplicationStatus = async (id, status) => {
    await api.patch(`/admin/applications/${id}`, { status });
    toast.success("Statut mis à jour.");
    load();
  };
  const removeApplication = async (id) => {
    await api.delete(`/admin/applications/${id}`);
    toast.success("Candidature supprimée.");
    load();
  };
  const downloadCv = async (app) => {
    try {
      const r = await api.get(`/admin/applications/${app.id}/cv`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = app.original_filename || "cv.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossible de télécharger le CV.");
    }
  };

  return (
    <div data-testid="services-manager">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-ivory">Maasaï Services</h2>
        <div className="flex gap-2" data-testid="services-subtabs">
          {[
            { id: "prestations", label: `Prestations (${items.length})` },
            { id: "reservations", label: `Réservations (${bookings.length})` },
            { id: "devis", label: `Devis (${quotes.length})` },
            { id: "candidatures", label: `Candidatures (${applications.length})` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setSub(t.id)}
              data-testid={`services-subtab-${t.id}`}
              className={`rounded-md px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors ${
                sub === t.id ? "bg-copper text-ivory" : "text-ivory/50 hover:text-gold"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {sub === "prestations" && (
        <div>
          <div className="mb-6 flex justify-end">
            <button onClick={() => setEditing({ ...emptyService })} className="btn-primary !h-10 !px-5 !text-[11px]" data-testid="service-add-button">
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              Ajouter une prestation
            </button>
          </div>

          {editing && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="service-edit-modal">
              <form onSubmit={save} className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto rounded-lg border border-white/10 bg-coal p-8">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-ivory">
                    {editing.id ? "Modifier la prestation" : "Nouvelle prestation"}
                  </h3>
                  <button type="button" onClick={() => setEditing(null)} aria-label="Fermer">
                    <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
                  </button>
                </div>
                <input required value={editing.name} onChange={set("name")} placeholder="Nom (ex : Ménage à domicile)" className="input-dark !h-11" data-testid="service-name-input" />
                <textarea rows={2} value={editing.description} onChange={set("description")} placeholder="Description courte (affichée sur la carte)" className="input-dark !h-auto py-3" data-testid="service-description-input" />
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                    Détails de la prestation (page « En savoir plus »)
                  </label>
                  <textarea rows={5} value={editing.details || ""} onChange={set("details")} placeholder={"Ce qui est inclus, le déroulement, les options… Une ligne par élément."} className="input-dark !h-auto py-3" data-testid="service-details-input" />
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Prix (€, 0 = sur devis)</label>
                    <input value={editing.price} onChange={set("price")} placeholder="0" className="input-dark !h-11" data-testid="service-price-input" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Unité</label>
                    <Select value={editing.unit || "none"} onValueChange={(v) => setEditing((s) => ({ ...s, unit: v === "none" ? "" : v }))}>
                      <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="service-unit-input">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-coal text-ivory">
                        <SelectItem value="none">Forfait</SelectItem>
                        <SelectItem value="/ heure">/ heure</SelectItem>
                        <SelectItem value="/ m²">/ m²</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">TVA</label>
                    <Select value={editing.tax || "ttc"} onValueChange={(v) => setEditing((s) => ({ ...s, tax: v }))}>
                      <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="service-tax-input">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-coal text-ivory">
                        <SelectItem value="ttc">TTC</SelectItem>
                        <SelectItem value="ht">HT</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Catégorie</label>
                    <Select value={editing.category || "none"} onValueChange={(v) => setEditing((s) => ({ ...s, category: v === "none" ? "" : v }))}>
                      <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="service-category-input">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-coal text-ivory">
                        <SelectItem value="none">Sans catégorie</SelectItem>
                        {SERVICE_CATEGORIES.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Ordre</label>
                    <input type="number" value={editing.order} onChange={set("order")} className="input-dark !h-11" data-testid="service-order-input" />
                  </div>
                </div>
                <div>
                  {editing.photo && (
                    <img src={editing.photo} alt="" className="mb-3 h-20 w-20 rounded-md border border-white/10 object-cover" />
                  )}
                  <input type="file" accept="image/*" onChange={uploadPhoto} className="text-xs text-ivory/60 file:mr-3 file:rounded-md file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-xs file:text-ivory" data-testid="service-photo-upload" />
                </div>
                <label className="flex items-center gap-3 text-sm text-ivory/70">
                  Visible
                  <Switch checked={editing.published} onCheckedChange={(v) => setEditing((s) => ({ ...s, published: v }))} data-testid="service-published-switch" />
                </label>
                <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="service-save-button">
                  {saving ? "Enregistrement…" : "Enregistrer"}
                </button>
              </form>
            </div>
          )}

          {items.length === 0 ? (
            <p className="text-sm text-ivory/40" data-testid="services-empty">
              Aucune prestation pour le moment. Ajoutez votre première prestation (ménage, nettoyage, entretien…).
            </p>
          ) : (
            <div className="space-y-2" data-testid="services-list">
              {items.map((s) => (
                <div key={s.id} className="flex items-center gap-4 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5" data-testid={`service-row-${s.id}`}>
                  <span className={`flex-1 truncate text-sm ${s.published ? "text-ivory" : "text-ivory/35 line-through"}`}>
                    {s.name}
                  </span>
                  <span className="text-sm text-gold">
                    {s.price > 0 ? `${String(s.price.toFixed(2)).replace(".", ",")} €${s.unit ? ` ${s.unit}` : ""} ${s.tax === "ht" ? "HT" : "TTC"}` : "Sur devis"}
                  </span>
                  <button onClick={() => setEditing({ ...s })} aria-label="Modifier" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold" data-testid={`service-edit-${s.id}`}>
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                  <button onClick={() => remove(s.id)} aria-label="Supprimer" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`service-delete-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {sub === "reservations" && (
        <div data-testid="service-bookings-list">
          {bookings.length === 0 ? (
            <p className="text-sm text-ivory/40">Aucune réservation pour le moment.</p>
          ) : (
            <div className="space-y-2">
              {bookings.map((b) => (
                <div key={b.id} className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5" data-testid={`service-booking-${b.id}`}>
                  <span className="min-w-40 text-sm font-semibold text-ivory">{b.name}</span>
                  <span className="text-xs text-ivory/50">
                    {b.date} · {b.time} · {b.quantity} × {b.amount.toFixed(2).replace(".", ",")} € au total
                  </span>
                  <span className="text-xs text-ivory/40">{b.address || "—"}</span>
                  <div className="ml-auto flex items-center gap-3">
                    <Select value={b.status} onValueChange={(v) => setBookingStatus(b.id, v)}>
                      <SelectTrigger className="!h-9 w-44 border-white/10 bg-white/5 text-xs text-ivory" data-testid={`service-booking-status-${b.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-coal text-ivory">
                        {Object.entries(BOOKING_STATUS).map(([v, l]) => (
                          <SelectItem key={v} value={v}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <button onClick={() => removeBooking(b.id)} aria-label="Supprimer" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`service-booking-delete-${b.id}`}>
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {sub === "devis" && (
        <div data-testid="service-quotes-list">
          {quotes.length === 0 ? (
            <p className="text-sm text-ivory/40">Aucune demande de devis pour le moment.</p>
          ) : (
            <div className="space-y-2">
              {quotes.map((q) => (
                <div key={q.id} className="rounded-lg border border-white/10 bg-coal/60 px-5 py-4" data-testid={`service-quote-${q.id}`}>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <span className="text-sm font-semibold text-ivory">{q.name}</span>
                    <span className="text-xs text-ivory/50">{q.email} · {q.phone || "—"}</span>
                    <span className="text-xs uppercase tracking-wider text-gold/70">{q.service || "Sur mesure"}</span>
                    <div className="ml-auto flex items-center gap-3">
                      <Select value={q.status} onValueChange={(v) => setQuoteStatus(q.id, v)}>
                        <SelectTrigger className="!h-9 w-36 border-white/10 bg-white/5 text-xs text-ivory" data-testid={`service-quote-status-${q.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="border-white/10 bg-coal text-ivory">
                          {Object.entries(QUOTE_STATUS).map(([v, l]) => (
                            <SelectItem key={v} value={v}>{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <button onClick={() => removeQuote(q.id)} aria-label="Supprimer" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`service-quote-delete-${q.id}`}>
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                  {q.message && <p className="mt-2 text-xs leading-relaxed text-ivory/50">{q.message}</p>}
                  {(q.surface || q.preferred_date || q.address) && (
                    <p className="mt-2 text-xs text-ivory/40" data-testid={`service-quote-infos-${q.id}`}>
                      {[
                        q.address && `Adresse : ${q.address}`,
                        q.surface && `Surface : ${q.surface} m²`,
                        q.preferred_date && `Date souhaitée : ${q.preferred_date}`,
                      ].filter(Boolean).join(" · ")}
                    </p>
                  )}
                  {(q.photos || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid={`service-quote-photos-${q.id}`}>
                      {q.photos.map((p, i) => (
                        <a key={i} href={p} target="_blank" rel="noreferrer">
                          <img src={p} alt="" className="h-14 w-14 rounded-md border border-white/10 object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {sub === "candidatures" && (
        <div data-testid="applications-list">
          {applications.length === 0 ? (
            <p className="text-sm text-ivory/40" data-testid="applications-empty">
              Aucune candidature pour le moment. Les CV déposés sur la page Services apparaîtront
              ici.
            </p>
          ) : (
            <div className="space-y-2">
              {applications.map((a) => (
                <div key={a.id} className="rounded-lg border border-white/10 bg-coal/60 px-5 py-4" data-testid={`application-${a.id}`}>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <span className="text-sm font-semibold text-ivory">{a.name}</span>
                    <span className="text-xs text-ivory/50">{a.email} · {a.phone || "—"}</span>
                    <span className="text-xs text-ivory/40">{(a.created_at || "").slice(0, 10)}</span>
                    <div className="ml-auto flex items-center gap-3">
                      <button
                        onClick={() => downloadCv(a)}
                        className="flex h-9 items-center gap-2 rounded-md border border-white/10 px-3 text-xs text-ivory/60 transition-colors hover:border-gold hover:text-gold"
                        data-testid={`application-cv-${a.id}`}
                      >
                        <Download className="h-3.5 w-3.5" strokeWidth={1.5} />
                        {a.original_filename || "CV"}
                      </button>
                      <Select value={a.status} onValueChange={(v) => setApplicationStatus(a.id, v)}>
                        <SelectTrigger className="!h-9 w-36 border-white/10 bg-white/5 text-xs text-ivory" data-testid={`application-status-${a.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="border-white/10 bg-coal text-ivory">
                          {Object.entries(APPLICATION_STATUS).map(([v, l]) => (
                            <SelectItem key={v} value={v}>{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <button onClick={() => removeApplication(a.id)} aria-label="Supprimer" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`application-delete-${a.id}`}>
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                  {a.message && <p className="mt-2 text-xs leading-relaxed text-ivory/50">{a.message}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
